import React, { useState, useEffect } from "react";
import { postRequest, patchRequest, getRequest, deleteRequest } from "services/api";
import clsx from "clsx";
import Select from "react-select";
import { IconDeviceFloppy, IconTrash } from "@tabler/icons-react";
import { formatDateToLocal } from "utils/utils";

const BatchUpdateCellsPage = ({ selectedCellsMap, onSuccess, onError }) => {
  const [shiftOptions, setShiftOptions] = useState([]);
  const [isLoadingShifts, setIsLoadingShifts] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [formData, setFormData] = useState({
    shift_id: "",
    is_day_off: false,
    status: "published",
  });
  
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  useEffect(() => {
    const fetchAllShifts = async () => {
      setIsLoadingShifts(true);
      try {
        const response = await getRequest("/ahr/shifts?all=1");
        if (response.success && response.data) {
          const opts = response.data.map(shift => ({
            value: shift.id,
            label: shift.name
          }));
          setShiftOptions(opts);
        }
      } catch (err) {
        console.error("Failed to load shifts", err);
      } finally {
        setIsLoadingShifts(false);
      }
    };
    fetchAllShifts();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.is_day_off && !formData.shift_id) {
      onError("Please select a shift or set as Day Off.");
      return;
    }

    setIsSubmitting(true);
    const cells = Array.from(selectedCellsMap.values());
    setProgress({ current: 0, total: cells.length });

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i];
      const payload = {
        user_id: cell.user.id,
        schedule_date: formatDateToLocal(cell.date),
        shift_id: formData.is_day_off ? null : formData.shift_id,
        is_day_off: formData.is_day_off ? 1 : 0,
        status: formData.status
      };

      try {
        if (cell.schedule) {
          // Update existing
          await patchRequest(`/ahr/schedules/${cell.schedule.id}`, payload);
        } else {
          // Create new
          await postRequest(`/ahr/schedules`, payload);
        }
        successCount++;
      } catch (err) {
        console.error("Error updating cell:", err);
        errorCount++;
      }
      setProgress({ current: i + 1, total: cells.length });
    }

    setIsSubmitting(false);

    if (errorCount === 0) {
      onSuccess(successCount);
    } else {
      onError(`Updated ${successCount} cells, but failed on ${errorCount} cells.`);
      onSuccess(successCount); // still call success to refresh
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`Are you sure you want to delete shifts for ${selectedCellsMap.size} selected cell(s)?`)) {
      return;
    }
    
    setIsDeleting(true);
    const cells = Array.from(selectedCellsMap.values());
    setProgress({ current: 0, total: cells.length });

    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i];
      try {
        if (cell.schedule) {
          await deleteRequest(`/ahr/schedules/${cell.schedule.id}`);
          successCount++;
        }
      } catch (err) {
        console.error("Error deleting cell:", err);
        errorCount++;
      }
      setProgress({ current: i + 1, total: cells.length });
    }

    setIsDeleting(false);

    if (errorCount === 0) {
      onSuccess(successCount);
    } else {
      onError(`Deleted ${successCount} cells, but failed on ${errorCount} cells.`);
      onSuccess(successCount); 
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      <div className="flex-grow-1">
        <div className="alert alert-info mb-4 d-flex align-items-center">
          <div className="me-3">
            <IconDeviceFloppy size={24} />
          </div>
          <div>
            You have selected <strong>{selectedCellsMap.size}</strong> cell(s) to update or delete.
          </div>
        </div>

        <form id="batchUpdateForm" onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label fw-bold">Shift</label>
            <Select
              options={shiftOptions}
              value={shiftOptions.find(opt => opt.value === formData.shift_id) || null}
              onChange={(selected) => setFormData(prev => ({ ...prev, shift_id: selected ? selected.value : "" }))}
              placeholder="Select shift..."
              isDisabled={isSubmitting || isDeleting || formData.is_day_off || isLoadingShifts}
              isLoading={isLoadingShifts}
              isClearable
            />
          </div>

          <div className="mb-4">
            <label className="form-check cursor-pointer mt-3">
              <input 
                type="checkbox" 
                className="form-check-input" 
                name="is_day_off" 
                checked={formData.is_day_off}
                onChange={handleChange}
                disabled={isSubmitting || isDeleting}
              />
              <span className="form-check-label fw-bold">Set as Day Off</span>
            </label>
          </div>

          <div className="mb-3">
            <label className="form-label fw-bold">Status</label>
            <select 
              className="form-select" 
              name="status" 
              value={formData.status}
              onChange={handleChange}
              disabled={isSubmitting || isDeleting}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>
        </form>

        {(isSubmitting || isDeleting) && (
          <div className="mt-4">
            <label className="form-label text-muted small">
              {isSubmitting ? "Updating..." : "Deleting..."} {progress.current} / {progress.total}
            </label>
            <div className="progress">
              <div 
                className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                role="progressbar" 
                style={{ width: `${(progress.current / progress.total) * 100}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-auto pt-3 border-top d-flex gap-2">
        <button 
          type="button" 
          className="btn btn-outline-danger w-50 fw-bold d-flex justify-content-center align-items-center gap-2"
          onClick={handleDeleteSelected}
          disabled={isSubmitting || isDeleting}
        >
          <IconTrash size={18} />
          {isDeleting ? "Deleting..." : "Delete Selected"}
        </button>
        <button 
          type="submit" 
          form="batchUpdateForm"
          className="btn btn-primary w-50 fw-bold"
          disabled={isSubmitting || isDeleting || (!formData.is_day_off && !formData.shift_id)}
        >
          {isSubmitting ? "Updating..." : "Update Selected"}
        </button>
      </div>
    </div>
  );
};

export default BatchUpdateCellsPage;
